"use client";

import {
  AudioLines,
  Check,
  ChevronDown,
  LoaderCircle,
  Search,
} from "lucide-react";
import Image from "next/image";
import { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import {
  defaultTtsModelId,
  defaultTtsVoiceId,
  getPublicElevenLabsVoiceId,
  getTtsModelOption,
  getTtsCreditsPerThousandCharacters,
  ttsModelOptions,
  isElevenLabsModelId,
  type SavedPublicVoice,
  type TtsModelId,
  type TtsModelOption,
  type TtsVoiceOption,
} from "@reelmagic/domain";
import { useFirebaseAuth } from "@/components/auth/AuthProvider";
import { getFirebaseClientServices } from "@/lib/firebase/client";
import { PublicVoiceDialog } from "./PublicVoiceDialog";
import { removePublicVoice } from "@/lib/generation/public-voices";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CreditMark } from "@/components/icons/CreditMark";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  createFieldLabelClassName,
  createFieldLabelTextClassName,
} from "./CreatePageShell";
import {
  SearchableAudioPickerField,
  type SearchableAudioPickerItem,
} from "./SearchableAudioPickerField";

const mediaAssetBaseUrl =
  process.env.NEXT_PUBLIC_MEDIA_ASSET_BASE_URL?.trim();

type PublicVoiceRecord = SavedPublicVoice & { archived?: boolean };

function getPreviewUrl(voice: TtsVoiceOption) {
  if (!mediaAssetBaseUrl) {
    throw new Error("NEXT_PUBLIC_MEDIA_ASSET_BASE_URL is required.");
  }

  return `${mediaAssetBaseUrl.replace(/\/+$/u, "")}/${voice.previewAssetKey.replace(/^\/+/, "")}`;
}

function formatCredits(value: number) {
  if (value === 0) return "0 credits";
  if (value < 0.01) return "<0.01 credits";
  if (value < 0.1) return `${value.toFixed(2)} credits`;
  return `${Number(value.toFixed(1))} credits`;
}

function getPriceLabel(model: TtsModelOption) {
  if (model.billing.type !== "input_character") return null;
  return `${formatCredits(getTtsCreditsPerThousandCharacters(model.id))} / 1k chars`;
}

const audioModelIconSources: Record<string, string> = {
  "elevenlabs/eleven-v4": "/ai-models/elevenlabs.svg",
  "elevenlabs/eleven-flash-v2.5": "/ai-models/elevenlabs.svg",
  "hexgrad/kokoro-82m": "/ai-models/kokoro.svg",
  "microsoft/mai-voice-2-flash": "/ai-models/mai.svg",
  "mistralai/voxtral-mini-tts-2603": "/ai-models/mistral.svg",
  "minimax/speech-2.8-turbo": "/ai-models/minimax.svg",
  "qwen/qwen-audio-3.0-tts-flash": "/ai-models/qwen.svg",
  "x-ai/grok-voice-tts-1.0": "/ai-models/grok.svg",
};

const audioModelBadges: Record<string, string> = {
  "hexgrad/kokoro-82m": "Cheapest",
  "minimax/speech-2.8-turbo": "Best quality",
  "mistralai/voxtral-mini-tts-2603": "Most expressive",
};

function AudioModelIcon({ modelId }: { modelId: string | undefined }) {
  const iconSrc = modelId ? audioModelIconSources[modelId] : undefined;

  return (
    <span className="flex size-9 shrink-0 items-center justify-center rounded-[11px] bg-white/[0.07] text-[var(--rm-text-soft)]">
      {iconSrc ? (
        <Image
          alt=""
          aria-hidden="true"
          className="size-5 brightness-0 invert"
          height={20}
          src={iconSrc}
          width={20}
        />
      ) : (
        <AudioLines aria-hidden="true" className="size-5" />
      )}
    </span>
  );
}

type TtsSelection = {
  ttsModelId: TtsModelId;
  voiceId: string;
};

type TtsModelSelectFieldProps = {
  inputCharacters?: number;
  label: string;
  onValueChange: (value: TtsSelection) => void;
  value: TtsModelId;
};

export function TtsModelSelectField({
  label,
  onValueChange,
  value,
}: TtsModelSelectFieldProps) {
  useFirebaseAuth();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selectedModel = getTtsModelOption(value);
  const models = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return ttsModelOptions
      .filter(
        (model) =>
          model.availability === "enabled" &&
          (!normalizedQuery ||
            `${model.label} ${model.description}`
              .toLowerCase()
              .includes(normalizedQuery)),
      )
      .sort((left, right) =>
        left.id === defaultTtsModelId ? -1 : right.id === defaultTtsModelId ? 1 : 0,
      );
  }, [query]);

  return (
    <div className={createFieldLabelClassName}>
      <span className={createFieldLabelTextClassName}>{label}</span>
      <Popover
        open={open}
        onOpenChange={(nextOpen) => {
          setOpen(nextOpen);
          if (!nextOpen) setQuery("");
        }}
      >
        <PopoverTrigger asChild>
          <button
            aria-expanded={open}
            aria-haspopup="listbox"
            className="group flex h-14 min-h-14 max-h-14 w-full items-center gap-3 rounded-2xl border border-border bg-white/[0.035] px-3 py-2 text-left shadow-sm outline-none transition duration-200 hover:border-white/20 hover:bg-white/[0.055] active:scale-[0.99] focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/35"
            type="button"
          >
            <AudioModelIcon modelId={selectedModel?.id} />
            <span className="grid min-w-0 flex-1 content-center">
              <span className="block h-5 truncate text-sm font-semibold leading-5 text-foreground">
                {selectedModel?.label ?? "Choose an audio model"}
              </span>
              {selectedModel ? (
                <span className="flex h-4 items-center gap-1.5 text-xs leading-4 text-[var(--rm-text-muted)]">
                  <CreditMark className="size-[0.9em] shrink-0" />
                  {getPriceLabel(selectedModel)}
                </span>
              ) : (
                <span className="block h-4 truncate text-xs leading-4 text-[var(--rm-text-muted)]">
                  Select a model
                </span>
              )}
            </span>
            <ChevronDown
              aria-hidden="true"
              className={cn(
                "size-4 shrink-0 text-[var(--rm-text-muted)] transition-transform",
                open && "rotate-180",
              )}
            />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-[min(430px,calc(100vw-32px))] overflow-hidden rounded-[20px] border-white/10 bg-popover p-0 shadow-2xl shadow-black/35"
          sideOffset={8}
        >
          <div className="relative border-b border-white/[0.07]">
            <Search
              aria-hidden="true"
              className="absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[var(--rm-text-muted)]"
            />
            <input
              autoFocus
              className="h-12 w-full bg-transparent pl-11 pr-4 text-sm outline-none placeholder:text-[var(--rm-text-muted)]"
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search audio models..."
              value={query}
            />
          </div>
          <div
            aria-label="Audio models"
            className="max-h-[min(420px,calc(100dvh-220px))] overscroll-contain overflow-y-auto p-2 touch-pan-y"
            onTouchMoveCapture={(event) => event.stopPropagation()}
            onWheelCapture={(event) => event.stopPropagation()}
            role="listbox"
          >
            {models.length ? (
              models.map((model) => {
                const isSelected = model.id === value;

                return (
                  <button
                    aria-selected={isSelected}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-[14px] px-3 py-3 text-left outline-none transition duration-150 hover:bg-white/[0.065] focus-visible:bg-white/[0.065] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/40",
                      isSelected && "bg-white/[0.075]",
                    )}
                    key={model.id}
                    onClick={() => {
                      if (isSelected) {
                        setOpen(false);
                        setQuery("");
                        return;
                      }

                      const firstVoice = model.voices[0];
                      if (!firstVoice) return;

                      onValueChange({
                        ttsModelId: model.id as TtsModelId,
                        voiceId: firstVoice.id,
                      });
                      setOpen(false);
                      setQuery("");
                    }}
                    role="option"
                    type="button"
                  >
                    <AudioModelIcon modelId={model.id} />
                    <span className="min-w-0 flex-1">
                      <span className="flex min-w-0 items-center gap-2">
                        <span className="truncate text-sm font-semibold">
                          {model.label}
                        </span>
                        {audioModelBadges[model.id] ? (
                          <span className="shrink-0 rounded-full border border-primary/25 bg-primary/10 px-1.5 py-0.5 text-[0.58rem] font-bold uppercase tracking-[0.11em] text-primary">
                            {audioModelBadges[model.id]}
                          </span>
                        ) : null}
                      </span>
                      <span className="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs text-[var(--rm-text-muted)]">
                        <span className="inline-flex shrink-0 items-center gap-1">
                          <CreditMark className="size-[0.9em]" />
                          {getPriceLabel(model)}
                        </span>
                        <span aria-hidden="true">·</span>
                        <span className="truncate">{model.description}</span>
                      </span>
                    </span>
                    <Check
                      aria-hidden="true"
                      className={cn(
                        "size-4 shrink-0 text-primary transition-opacity",
                        isSelected ? "opacity-100" : "opacity-0",
                      )}
                    />
                  </button>
                );
              })
            ) : (
              <p className="px-4 py-10 text-center text-sm text-[var(--rm-text-muted)]">
                No audio models match “{query}”.
              </p>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

type VoiceSelectFieldProps = {
  label: string;
  onValueChange: (value: TtsSelection) => void;
  ttsModelId: TtsModelId;
  voiceId: string;
};

export function VoiceSelectField({
  label,
  onValueChange,
  ttsModelId,
  voiceId,
}: VoiceSelectFieldProps) {
  const { user } = useFirebaseAuth();
  const userId = user?.uid;
  const [publicVoices, setPublicVoices] = useState<PublicVoiceRecord[]>([]);
  const [voiceLoadError, setVoiceLoadError] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [removalTarget, setRemovalTarget] = useState<SavedPublicVoice | null>(null);
  const [removing, setRemoving] = useState(false);
  const [removalError, setRemovalError] = useState<string | null>(null);
  const isElevenLabs = isElevenLabsModelId(ttsModelId);
  useEffect(() => {
    setPublicVoices([]);
    setVoiceLoadError(false);
    setAddOpen(false);
    setRemovalTarget(null);
    if (!userId || !isElevenLabs) return;
    const { firestore } = getFirebaseClientServices();
    return onSnapshot(collection(firestore, "users", userId, "publicVoices"), (snapshot) => {
      setPublicVoices(snapshot.docs.map((doc) => doc.data() as PublicVoiceRecord));
      setVoiceLoadError(false);
    }, () => setVoiceLoadError(true));
  }, [userId, isElevenLabs]);
  useEffect(() => { setAddOpen(false); setRemovalTarget(null); }, [ttsModelId]);
  useEffect(() => {
    if (publicVoices.some((voice) => voice.archived && voice.modelId === ttsModelId && voice.voiceId === voiceId)) {
      onValueChange({ ttsModelId, voiceId: defaultTtsVoiceId });
    }
  }, [publicVoices, ttsModelId, voiceId, onValueChange]);
  const selectedModel = getTtsModelOption(ttsModelId);
  const voices = useMemo<SearchableAudioPickerItem[]>(
    () =>
      [...publicVoices.filter((voice) => !voice.archived && voice.modelId === ttsModelId).sort((a, b) => a.label.localeCompare(b.label)).map((voice) => ({
        id: voice.voiceId,
        label: voice.label,
        description: "Saved public voice",
        descriptionAction: { label: "Remove", onClick: () => { setRemovalError(null); setRemovalTarget(voice); } },
        searchText: voice.voiceId,
        previewable: false,
      })), ...(selectedModel?.voices ?? []).map((voice) => ({
        id: voice.id,
        label: voice.label,
        previewUrl: getPreviewUrl(voice),
      }))],
    [selectedModel, publicVoices, ttsModelId],
  );

  async function confirmRemoval() {
    if (!removalTarget || removing) return;
    setRemoving(true);
    setRemovalError(null);
    try {
      const providerVoiceId = getPublicElevenLabsVoiceId(removalTarget.voiceId);
      if (!providerVoiceId) throw new Error("This saved voice ID is not valid.");
      await removePublicVoice({ modelId: removalTarget.modelId as TtsModelId, providerVoiceId });
      setPublicVoices((current) => current.map((voice) => voice.id === removalTarget.id ? { ...voice, archived: true } : voice));
      setRemovalTarget(null);
    } catch (error) {
      setRemovalError(error instanceof Error ? error.message.replace(/^Firebase:\s*/, "").replace(/\s*\(functions\/[^)]+\)\.?$/u, "") : "This voice could not be removed. Try again.");
    } finally {
      setRemoving(false);
    }
  }

  return (
    <div className="grid gap-2">
    <SearchableAudioPickerField
      action={isElevenLabs ? { label: "Add public voice", onClick: () => setAddOpen(true) } : undefined}
      emptyLabel="No matching voices."
      items={voices}
      label={label}
      listLabel={`${selectedModel?.label ?? "Audio model"} voices`}
      onValueChange={(nextVoiceId) =>
        onValueChange({ ttsModelId, voiceId: nextVoiceId })
      }
      previewContextKey={ttsModelId}
      previewErrorLabel="This voice preview could not play."
      searchPlaceholder={`Search ${selectedModel?.label ?? "model"} voices...`}
      triggerDescription={selectedModel?.label ?? "Audio voice"}
      value={voiceId}
    />
    {voiceLoadError ? <p role="alert" className="text-xs text-destructive">Saved voices could not load. Reload the page to try again.</p> : null}
    {isElevenLabs ? <PublicVoiceDialog key={`${user?.uid}:${ttsModelId}`} open={addOpen} onOpenChange={setAddOpen} modelId={ttsModelId} onSaved={(saved) => {
      setPublicVoices((current) => [...current.filter((voice) => voice.id !== saved.id), saved]);
      onValueChange({ ttsModelId, voiceId: saved.voiceId });
    }} /> : null}
    <AlertDialog open={!!removalTarget} onOpenChange={(open) => { if (!open && !removing) setRemovalTarget(null); }}>
      <AlertDialogContent className="max-w-[420px] gap-5 rounded-[24px] border-white/10 p-6">
        <AlertDialogHeader>
          <AlertDialogTitle>Remove saved voice?</AlertDialogTitle>
          <AlertDialogDescription>Remove “{removalTarget?.label}” from your saved voices? You can add it again later.</AlertDialogDescription>
        </AlertDialogHeader>
        {removalError ? <p role="alert" className="rounded-xl border border-destructive/20 bg-destructive/10 px-3.5 py-3 text-sm leading-5 text-destructive">{removalError}</p> : null}
        <AlertDialogFooter className="border-t border-white/[0.07] pt-4">
          <AlertDialogCancel className="h-11 rounded-xl" disabled={removing}>Cancel</AlertDialogCancel>
          <AlertDialogAction className="h-11 rounded-xl border-destructive/40 bg-destructive text-destructive-foreground shadow-none hover:bg-destructive/90" disabled={removing} onClick={(event) => { event.preventDefault(); void confirmRemoval(); }}>
            {removing ? <LoaderCircle className="animate-spin" /> : null}
            {removing ? "Removing…" : "Remove voice"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </div>
  );
}
